// Login das duas superfícies (`spec/17-frontend.md` §8.1 e §8.2). Um arquivo só: o
// formulário é o mesmo; o admin não tem cadastro nem auto-serviço de senha.

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useRouter } from '@tanstack/react-router';
import { Orbit } from 'loading-dev';
import { type ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  CAPTCHA_ERROR_MESSAGE,
  isCaptchaError,
  requestTwoFactorRecovery,
  signIn,
  verifyTotp,
} from '../api/auth.ts';
import { errorMessageOf, isApiError } from '../api/errors.ts';
import { SESSION_QUERY_KEY } from '../api/session.ts';
import { surface } from '../app/surface.ts';
import { AuthShell, FormError } from '../components/app/auth-shell.tsx';
import { TURNSTILE_ENABLED, Turnstile } from '../components/app/turnstile.tsx';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import {
  EMAIL_NOT_VERIFIED_CODE,
  ResendVerification,
} from '../features/auth/resend-verification.tsx';
import { nextPathSchema } from '../features/oauth/next-path.ts';

// `next` só pode ser caminho interno: `//evil.com` e `https://evil.com` viram redirect
// aberto logo depois do login.
const searchSchema = z.object({
  next: nextPathSchema,
  /** WEB-140: o connect já sabe o e-mail do vendedor; vem preenchido (e editável). */
  email: z.string().max(320).optional(),
  /** AUTH-05: `/recover-2fa` manda para cá com o aviso do que acabou de acontecer. */
  recovered: z.boolean().optional(),
  /** Por que a sessão acabou, quando o gate consegue explicar (`app/guards.tsx`). */
  reason: z.literal('ip-changed').optional().catch(undefined),
});

const credentialsSchema = z.object({
  email: z.email('Informe um email válido'),
  password: z.string().min(1, 'Informe a senha'),
});

type Credentials = z.infer<typeof credentialsSchema>;

/** Texto de botão em andamento. Spinner padrão do projeto (loading-dev), tamanho pela prop. */
function Busy({ children }: { children: ReactNode }) {
  return (
    <>
      <span aria-hidden="true" className="flex">
        <Orbit size={16} color="currentColor" />
      </span>
      {children}
    </>
  );
}

function Login() {
  const { next, recovered, reason, email } = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const isAdmin = surface() === 'admin';
  const [needsTotp, setNeedsTotp] = useState(false);
  const [totp, setTotp] = useState('');
  // AUTH-05: "perdi meu app". A senha já passou neste ponto do fluxo, e é ela que o
  // `POST .../two-factor/recovery/request` exige no corpo (`spec/03` §4.3).
  const [recoverySent, setRecoverySent] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  // Email que o login barrou por não estar confirmado: habilita o reenvio abaixo do erro.
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  // Token de uso único (SEC-01): `attempt` é a chave do widget, bumpar remonta e pede
  // um desafio novo. O `/sign-in/email` é o endpoint com captcha; o segundo fator não.
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);
  const [captchaAttempt, setCaptchaAttempt] = useState(0);

  const resetCaptcha = (): void => {
    setCaptchaToken(undefined);
    setCaptchaAttempt((attempt) => attempt + 1);
  };

  const form = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: email ?? '', password: '' },
  });

  const done = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    if (next !== undefined) {
      // `next` já passou pelo regex de caminho interno e pode ter query (`/oauth/authorize?...`),
      // que o `to` do `navigate` não interpreta.
      router.history.push(next);
      return;
    }
    await navigate({ to: isAdmin ? '/admin' : '/app' });
  };

  const submitCredentials = form.handleSubmit(async (values) => {
    setError(undefined);
    setUnverifiedEmail(undefined);
    setSubmitting(true);
    try {
      const result = await signIn(values, captchaToken);
      if (result.twoFactorRedirect === true) {
        setNeedsTotp(true);
        return;
      }
      await done();
    } catch (caught) {
      setError(isCaptchaError(caught) ? CAPTCHA_ERROR_MESSAGE : errorMessageOf(caught));
      if (isApiError(caught) && caught.code === EMAIL_NOT_VERIFIED_CODE) {
        setUnverifiedEmail(values.email);
      }
    } finally {
      setSubmitting(false);
      resetCaptcha();
    }
  });

  const requestRecovery = async (): Promise<void> => {
    setError(undefined);
    setSubmitting(true);
    try {
      const { email, password } = form.getValues();
      await requestTwoFactorRecovery({ email, password });
      // A resposta é 200 com ou sem conta: a tela nunca diz se o email existe.
      setRecoverySent(true);
    } catch (caught) {
      setError(errorMessageOf(caught));
    } finally {
      setSubmitting(false);
    }
  };

  const submitTotp = async (): Promise<void> => {
    setError(undefined);
    setSubmitting(true);
    try {
      await verifyTotp(totp.trim());
      await done();
    } catch (caught) {
      setError(errorMessageOf(caught));
    } finally {
      setSubmitting(false);
    }
  };

  if (needsTotp) {
    return (
      <AuthShell
        title="Segundo fator"
        description="Digite o código do seu app de autenticação ou um backup code."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submitTotp();
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="totp">Código</Label>
            <Input
              id="totp"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              value={totp}
              onChange={(event) => {
                setTotp(event.target.value);
              }}
              className="num h-11 bg-input text-16 tracking-[0.3em]"
            />
          </div>
          <FormError message={error} />
          <Button type="submit" className="h-11" disabled={submitting || totp.trim() === ''}>
            {submitting ? <Busy>Verificando…</Busy> : 'Entrar'}
          </Button>
          {/* A página de login é compartilhada: o admin não tem auto-serviço de 2FA. */}
          {!isAdmin &&
            (recoverySent ? (
              <p className="text-center text-11 text-muted">
                Se a conta existir, o link para desligar a verificação em duas etapas está a caminho
                do seu email.
              </p>
            ) : (
              <button
                type="button"
                disabled={submitting}
                onClick={() => {
                  void requestRecovery();
                }}
                className="text-center text-13 text-muted underline-offset-4 hover:text-text hover:underline disabled:opacity-50"
              >
                Perdi meu app de autenticação
              </button>
            ))}
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={isAdmin ? 'Backoffice' : 'Entrar'}
      description={
        isAdmin ? 'Acesso restrito aos operadores da Lezty Pay.' : 'Acesse o painel da sua conta.'
      }
      // AUTH-06: não existe mais link de cadastro. Quem entra é convidado pelo admin e
      // chega por `/signup?invite=<token>`; um link aqui só levaria a uma tela fechada.
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submitCredentials();
        }}
      >
        <div className="flex flex-col gap-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            className="h-11 bg-input"
            {...form.register('email')}
          />
          <FormError message={form.formState.errors.email?.message} />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="password">Senha</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            className="h-11 bg-input"
            {...form.register('password')}
          />
          <FormError message={form.formState.errors.password?.message} />
        </div>

        {reason === 'ip-changed' && (
          <p
            role="status"
            className="rounded-card border border-border bg-secondary px-4 py-3 text-11 text-muted-foreground"
          >
            Sua sessão foi encerrada porque o endereço IP mudou. Desative VPN ou o Private Relay da
            Apple para usar o backoffice.
          </p>
        )}

        {recovered === true && (
          <p
            role="status"
            className="rounded-card border border-border bg-panel px-4 py-3 text-11 text-muted"
          >
            Verificação em duas etapas desligada. Entre com email e senha; os saques ficam
            bloqueados por 24 horas.
          </p>
        )}

        <FormError message={error} />
        {unverifiedEmail !== undefined && <ResendVerification email={unverifiedEmail} />}

        <Turnstile key={captchaAttempt} onToken={setCaptchaToken} />

        <Button
          type="submit"
          className="h-11"
          disabled={submitting || (TURNSTILE_ENABLED && captchaToken === undefined)}
        >
          {submitting ? <Busy>Entrando…</Busy> : 'Entrar'}
        </Button>

        {!isAdmin && (
          <Link
            to="/forgot-password"
            className="text-center text-13 text-muted underline-offset-4 hover:text-text hover:underline"
          >
            Esqueci minha senha
          </Link>
        )}
      </form>
    </AuthShell>
  );
}

export const Route = createFileRoute('/login/')({
  validateSearch: searchSchema,
  component: Login,
});
